import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, ScanSearch, Upload } from 'lucide-react';
import { vendorRfqsAPI } from '../../services/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const BASIS_OPTIONS = [
  { value: 'per_piece', label: 'per piece' },
  { value: 'per_100', label: 'per 100 pcs' },
  { value: 'per_1000', label: 'per 1000 pcs' },
];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];
const PIECES = { per_piece: 1, per_100: 100, per_1000: 1000 };

export const VendorQuoteUploadDialog = ({ rfq, open, onOpenChange, onApplied }) => {
  const [record, setRecord] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [header, setHeader] = useState({ currency: 'INR', price_basis: 'per_piece', exchange_rate: '', quote_reference: '', remarks: '' });
  const [rows, setRows] = useState([]);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) {
      clearTimeout(timer.current);
      setRecord(null); setRows([]);
    }
  }, [open]);

  const poll = (importId) => {
    timer.current = setTimeout(async () => {
      try {
        const response = await vendorRfqsAPI.getQuoteImport(importId);
        applyRecord(response.data);
        if (response.data.status === 'processing') poll(importId);
      } catch { toast.error('Could not read the upload status'); }
    }, 3000);
  };

  const applyRecord = (data) => {
    setRecord(data);
    if (data.status === 'review_required') {
      setHeader({
        currency: data.header?.currency || rfq?.currency || 'INR',
        price_basis: data.header?.price_basis || rfq?.price_basis || 'per_piece',
        exchange_rate: data.header?.exchange_rate || '',
        quote_reference: data.header?.quote_reference || '',
        remarks: data.header?.notes || '',
      });
      setRows((data.rows || []).map((row) => ({ ...row, include: Boolean(row.inquiry_product_id) })));
    }
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const response = await vendorRfqsAPI.uploadQuote(rfq.id, file);
      setRecord(response.data);
      toast.success('Vendor quotation uploaded — reading it now');
      poll(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const setRow = (id, patch) => setRows((current) => current.map((row) => row.id === id ? { ...row, ...patch } : row));

  const items = rfq?.items || [];
  const perPiece = (row) => {
    const price = Number(row.unit_price);
    if (!Number.isFinite(price)) return null;
    return price / (PIECES[header.price_basis] || 1);
  };

  const confirmable = useMemo(
    () => rows.filter((row) => row.include && row.inquiry_product_id && Number(row.unit_price) > 0),
    [rows],
  );

  const confirm = async () => {
    if (!confirmable.length) return toast.error('Match at least one row to an RFQ item with a price');
    setSaving(true);
    try {
      await vendorRfqsAPI.confirmQuoteImport(record.id, {
        currency: header.currency,
        price_basis: header.price_basis,
        exchange_rate: header.exchange_rate ? Number(header.exchange_rate) : null,
        quote_reference: header.quote_reference || null,
        remarks: header.remarks || null,
        items: confirmable.map((row) => ({
          inquiry_product_id: row.inquiry_product_id,
          unit_price: Number(row.unit_price),
          net_weight_grams: row.net_weight_grams ? Number(row.net_weight_grams) : null,
          moq: row.moq ? Number(row.moq) : null,
          lead_time: row.lead_time || null,
          remarks: row.remarks || null,
        })),
      });
      toast.success(`${confirmable.length} vendor price(s) applied to the comparison`);
      onOpenChange(false);
      await onApplied();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not apply vendor pricing');
    } finally { setSaving(false); }
  };

  if (!rfq) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="vendor-quote-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload vendor quotation</DialogTitle>
          <DialogDescription>{rfq.vendor_name} · {rfq.rfq_number} — the file is read by AI and every price is shown for your review before it reaches the comparison.</DialogDescription>
        </DialogHeader>

        {!record && (
          <label className="flex cursor-pointer flex-col items-center gap-3 border-2 border-dashed p-10 text-center">
            <Upload className="h-8 w-8 text-blue-600" />
            <span className="font-medium">Select the vendor's quotation (PDF, Excel, CSV or photo)</span>
            <span className="text-xs text-muted-foreground">Up to 25 MB. The original file is retained under the inquiry's Vendor responses.</span>
            <Button asChild disabled={uploading}><span data-testid="vendor-quote-upload-button">
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Choose file
            </span></Button>
            <input type="file" className="hidden" onChange={upload} data-testid="vendor-quote-upload-input" />
          </label>
        )}

        {record?.status === 'processing' && (
          <div className="flex flex-col items-center gap-3 py-10" data-testid="vendor-quote-processing">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="font-medium">Reading {record.filename}…</p>
            <p className="text-sm text-muted-foreground">Large quotations can take a minute or two.</p>
          </div>
        )}

        {record?.status === 'needs_manual_review' && (
          <div className="border border-amber-300 bg-amber-50 p-4" data-testid="vendor-quote-failed">
            <p className="font-medium text-amber-900">The quotation could not be read automatically.</p>
            <p className="text-sm text-amber-800">{record.extraction_error}</p>
            <p className="mt-2 text-sm text-amber-800">The file is retained. Use the "Pricing" button to enter the prices manually.</p>
          </div>
        )}

        {record?.status === 'review_required' && (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="space-y-1"><Label>Currency</Label>
                <select className="h-10 w-full border bg-white px-2 text-sm" value={header.currency}
                  onChange={(event) => setHeader({ ...header, currency: event.target.value })} data-testid="vendor-quote-currency-select">
                  {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label>Price basis</Label>
                <select className="h-10 w-full border bg-white px-2 text-sm" value={header.price_basis}
                  onChange={(event) => setHeader({ ...header, price_basis: event.target.value })} data-testid="vendor-quote-basis-select">
                  {BASIS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label>Exchange rate to INR</Label>
                <Input type="number" step="0.0001" value={header.exchange_rate} placeholder={header.currency === 'INR' ? 'Not needed' : 'e.g. 86.5'}
                  disabled={header.currency === 'INR'}
                  onChange={(event) => setHeader({ ...header, exchange_rate: event.target.value })} data-testid="vendor-quote-exchange-rate-input" />
              </div>
              <div className="space-y-1"><Label>Vendor quote reference</Label>
                <Input value={header.quote_reference} onChange={(event) => setHeader({ ...header, quote_reference: event.target.value })}
                  data-testid="vendor-quote-reference-input" />
              </div>
            </div>

            <div className="max-h-[340px] overflow-y-auto border" data-testid="vendor-quote-review-rows">
              {rows.map((row) => (
                <div key={row.id} className={`grid gap-3 border-b p-3 lg:grid-cols-[1fr_230px_140px_120px_140px]`} data-testid={`vendor-quote-row-${row.id}`}>
                  <div>
                    <p className="font-mono text-sm">{row.part_number || '—'}</p>
                    <p className="text-sm text-muted-foreground">{row.description}</p>
                    {row.inquiry_product_id ? (
                      <Badge variant="outline" className="mt-1 border-emerald-300 text-emerald-800" data-testid={`vendor-quote-match-${row.id}`}>
                        Matched {row.match_score ? `· ${Math.round(row.match_score * 100)}%` : ''} {row.match_reason ? `· ${row.match_reason}` : ''}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="mt-1 border-amber-400 bg-amber-50 text-amber-900" data-testid={`vendor-quote-unmatched-${row.id}`}>
                        No match — choose the RFQ item
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">RFQ item</Label>
                    <select className={`h-9 w-full border px-2 text-xs ${row.inquiry_product_id ? 'bg-white' : 'border-amber-400 bg-amber-50'}`}
                      value={row.inquiry_product_id || ''}
                      onChange={(event) => setRow(row.id, { inquiry_product_id: event.target.value || null, include: Boolean(event.target.value) })}
                      data-testid={`vendor-quote-item-select-${row.id}`}>
                      <option value="">— Choose the RFQ item —</option>
                      {items.map((item) => (
                        <option key={item.inquiry_product_id} value={item.inquiry_product_id}>
                          {item.part_number || 'Unlinked'} — {(item.description || '').slice(0, 34)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Price ({header.currency})</Label>
                    <Input type="number" step="0.0001" value={row.unit_price ?? ''} className="h-9"
                      onChange={(event) => setRow(row.id, { unit_price: event.target.value })}
                      data-testid={`vendor-quote-price-${row.id}`} />
                    {perPiece(row) !== null && (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid={`vendor-quote-per-piece-${row.id}`}>
                        = {header.currency} {perPiece(row).toFixed(4)} / pc
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Wt / pc (g)</Label>
                    <Input type="number" step="0.001" value={row.net_weight_grams ?? ''} className="h-9"
                      onChange={(event) => setRow(row.id, { net_weight_grams: event.target.value })}
                      data-testid={`vendor-quote-weight-${row.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Lead time / MOQ</Label>
                    <Input value={row.lead_time ?? ''} className="h-9 mb-1" placeholder="Lead time"
                      onChange={(event) => setRow(row.id, { lead_time: event.target.value })}
                      data-testid={`vendor-quote-lead-time-${row.id}`} />
                    <Input type="number" value={row.moq ?? ''} className="h-9" placeholder="MOQ"
                      onChange={(event) => setRow(row.id, { moq: event.target.value })}
                      data-testid={`vendor-quote-moq-${row.id}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground" data-testid="vendor-quote-summary">
              {rows.length} row(s) read · {confirmable.length} ready to apply
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vendor-quote-cancel-button">Close</Button>
          {record?.status === 'review_required' && (
            <Button onClick={confirm} disabled={saving || !confirmable.length} data-testid="vendor-quote-confirm-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Apply {confirmable.length} price(s)
            </Button>
          )}
          {record?.status === 'processing' && (
            <Button disabled data-testid="vendor-quote-reading-button"><ScanSearch className="mr-2 h-4 w-4" />Reading…</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
